import { useState } from "react";
import { 
  ScrollText, 
  Building2, 
  Calendar, 
  Euro, 
  ExternalLink,
  Edit2,
  Save,
  X,
  Plus,
  Trash2,
  Sparkles,
  Wand2
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import EnrichmentPopup, { EnrichButton } from "@/components/EnrichmentPopup";
import { useApi } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Inline editable field with AI enrichment option
function InlineEdit({ value, onSave, type = "text", placeholder = "", onEnrich }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value || "");

  const handleSave = () => {
    onSave(editValue);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        {type === "textarea" ? (
          <Textarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="text-sm h-16"
            autoFocus
          />
        ) : (
          <Input
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            className="h-7 text-sm"
            autoFocus
          />
        )}
        <Button size="sm" className="h-6 w-6 p-0" onClick={handleSave}>
          <Save className="w-3 h-3" />
        </Button>
        <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => setIsEditing(false)}>
          <X className="w-3 h-3" />
        </Button>
      </div>
    );
  }

  return (
    <div className="group flex items-center gap-1">
      <span className="font-medium text-stone-800 text-sm">{value || placeholder || "N/A"}</span>
      <Button 
        size="sm" 
        variant="ghost" 
        className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity"
        onClick={() => setIsEditing(true)}
        data-testid="edit-field-btn"
      >
        <Edit2 className="w-3 h-3" />
      </Button>
      {onEnrich && (
        <Button
          size="sm"
          variant="ghost"
          className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-blue-500 hover:text-blue-600"
          onClick={onEnrich}
          title="AI Enrich"
          data-testid="ai-enrich-field-btn"
        >
          <Sparkles className="w-3 h-3" />
        </Button>
      )}
    </div>
  );
}

// Editable PRO list for waste streams
function EditablePROList({ pros = [], onUpdate }) {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newPro, setNewPro] = useState({ name: "", website: "" });

  const handleAddPRO = () => {
    if (!newPro.name) {
      toast.error("PRO name is required");
      return;
    }
    onUpdate([...pros, newPro]);
    setNewPro({ name: "", website: "" });
    setShowAddDialog(false);
    toast.success("PRO added");
  };

  const handleRemovePRO = (index) => {
    const updated = pros.filter((_, i) => i !== index);
    onUpdate(updated);
    toast.success("PRO removed");
  };

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {pros.map((pro, idx) => (
          <div
            key={idx}
            className="flex items-center justify-between p-2 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors text-sm group"
          >
            <a
              href={pro.website}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 flex-1 min-w-0"
            >
              <span className="font-medium text-stone-700 truncate">{pro.name}</span>
              <ExternalLink className="w-3 h-3 text-stone-400 flex-shrink-0" />
            </a>
            <Button
              size="sm"
              variant="ghost"
              className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100 text-red-500"
              onClick={() => handleRemovePRO(idx)}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          </div>
        ))}
        <Button
          variant="outline"
          size="sm"
          className="h-9"
          onClick={() => setShowAddDialog(true)}
        >
          <Plus className="w-3.5 h-3.5 mr-1" />
          Add PRO
        </Button>
      </div>

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Add PRO</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={newPro.name}
                onChange={(e) => setNewPro({...newPro, name: e.target.value})}
                placeholder="PRO name"
              />
            </div>
            <div className="space-y-2">
              <Label>Website</Label>
              <Input
                value={newPro.website}
                onChange={(e) => setNewPro({...newPro, website: e.target.value})}
                placeholder="https://..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
            <Button onClick={handleAddPRO}>Add PRO</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Editable scope/items list
function EditableScopeList({ items = [], onUpdate, label = "Items" }) {
  const [newItem, setNewItem] = useState("");

  const handleAdd = () => {
    if (!newItem.trim()) return;
    onUpdate([...items, newItem.trim()]);
    setNewItem("");
  };

  const handleRemove = (index) => {
    onUpdate(items.filter((_, i) => i !== index));
  };

  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {items.map((item, idx) => (
          <Badge key={idx} variant="secondary" className="text-xs pr-1 group">
            {item}
            <button 
              onClick={() => handleRemove(idx)} 
              className="ml-1 opacity-50 group-hover:opacity-100 hover:text-red-500"
            >
              <X className="w-3 h-3" />
            </button>
          </Badge>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          placeholder={`Add ${label.toLowerCase()}...`}
          className="h-7 text-sm"
          onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
        />
        <Button size="sm" className="h-7" onClick={handleAdd}>
          <Plus className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

// Main Editable EPR Category Section
export default function EditableEPRCategorySection({ 
  countryCode, 
  category, 
  data, 
  categoryName,
  onRefresh 
}) {
  const api = useApi();
  const [localData, setLocalData] = useState(data || {});
  const [saving, setSaving] = useState(false);
  const [enrichmentOpen, setEnrichmentOpen] = useState(false);
  const [enrichmentField, setEnrichmentField] = useState(null);

  const saveChanges = async (updatedData) => {
    setSaving(true);
    try {
      await api.patch(`/api/country-customizations/${countryCode}/field`, {
        field_path: category,
        new_value: updatedData,
        notes: `Updated ${categoryName} data`
      });
      setLocalData(updatedData);
      toast.success(`${categoryName} updated`);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error("Error saving:", error);
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    const updated = { ...localData, [field]: value };
    setLocalData(updated);
    saveChanges(updated);
  };

  const updateNestedField = (parent, field, value) => {
    const updated = {
      ...localData,
      [parent]: { ...localData[parent], [field]: value }
    };
    setLocalData(updated);
    saveChanges(updated);
  };

  const openEnrichment = (fieldName, fieldType, currentValue) => {
    setEnrichmentField({ fieldName, fieldType, currentValue });
    setEnrichmentOpen(true);
  };

  const handleEnrichmentAccept = (value) => {
    if (enrichmentField) {
      if (enrichmentField.fieldName.includes('.')) {
        const [parent, field] = enrichmentField.fieldName.split('.');
        updateNestedField(parent, field, value);
      } else {
        updateField(enrichmentField.fieldName, value);
      }
    }
  };

  const bulkEnrich = async () => {
    try {
      setSaving(true);
      const response = await api.post(`/api/enrichment/bulk`, {
        country_code: countryCode,
        category: category,
        user_prompt: `Provide complete ${categoryName} EPR compliance data for ${countryCode}`
      });
      
      if (response.data.success && response.data.data) {
        const enrichedData = response.data.data;
        // Remove metadata fields
        delete enrichedData.confidence;
        delete enrichedData.explanation;
        
        setLocalData(enrichedData);
        await saveChanges(enrichedData);
        toast.success(`${categoryName} data enriched with AI`);
      } else {
        toast.error("Failed to enrich data");
      }
    } catch (error) {
      console.error("Bulk enrich error:", error);
      toast.error("Failed to enrich data");
    } finally {
      setSaving(false);
    }
  };

  const getStatusStyle = (status) => {
    switch(status) {
      case 'mandatory': return 'bg-green-100 text-green-700';
      case 'planned': return 'bg-amber-100 text-amber-700';
      case 'voluntary': return 'bg-blue-100 text-blue-700';
      default: return 'bg-stone-100 text-stone-700';
    }
  };

  // If no data exists, show option to initialize
  if (!data && !localData.status) {
    return (
      <div className="card p-8 text-center">
        <ScrollText className="w-12 h-12 text-stone-300 mx-auto mb-3" />
        <h3 className="text-sm font-medium text-stone-700 mb-1">
          {categoryName} EPR not configured
        </h3>
        <p className="text-xs text-stone-500 mb-4">
          Add {categoryName} compliance data for {countryCode}
        </p>
        <Button 
          size="sm" 
          onClick={() => {
            const initial = {
              status: 'planned',
              regulation_name: '',
              effective_date: '',
              major_pros: [],
              scope: [],
              notes: ''
            };
            setLocalData(initial);
            saveChanges(initial);
          }}
          data-testid={`init-${category}`}
        >
          <Plus className="w-4 h-4 mr-1.5" />
          Add {categoryName} Data
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {saving && (
        <div className="fixed top-4 right-4 bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm z-50">
          Saving...
        </div>
      )}

      {/* Bulk AI Enrich Button */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={bulkEnrich}
          disabled={saving}
          className="text-blue-600 border-blue-200 hover:bg-blue-50"
          data-testid="bulk-enrich-btn"
        >
          <Wand2 className="w-4 h-4 mr-1.5" />
          AI Fill All Fields
        </Button>
      </div>

      {/* Overview Cards - Editable */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="card p-3">
          <div className="flex items-center gap-1.5 text-stone-500 mb-1">
            <ScrollText className="w-3.5 h-3.5" />
            <span className="text-xs">Regulation</span>
          </div>
          <InlineEdit 
            value={localData.regulation_name}
            onSave={(v) => updateField('regulation_name', v)}
            placeholder="Enter regulation name"
            onEnrich={() => openEnrichment('regulation_name', 'text', localData.regulation_name)}
          />
        </div>
        
        <div className="card p-3">
          <div className="flex items-center gap-1.5 text-stone-500 mb-1">
            <Building2 className="w-3.5 h-3.5" />
            <span className="text-xs">Status</span>
          </div>
          <Select 
            value={localData.status || 'planned'} 
            onValueChange={(v) => updateField('status', v)}
          >
            <SelectTrigger className="h-7 text-sm w-[140px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="mandatory">Mandatory</SelectItem>
              <SelectItem value="planned">Planned</SelectItem>
              <SelectItem value="voluntary">Voluntary</SelectItem>
              <SelectItem value="none">None</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="card p-3">
          <div className="flex items-center gap-1.5 text-stone-500 mb-1">
            <Calendar className="w-3.5 h-3.5" />
            <span className="text-xs">{category === 'tyres' ? 'Collection Rate' : 'Effective Date'}</span>
          </div>
          <InlineEdit 
            value={category === 'tyres' ? localData.collection_rate : localData.effective_date}
            onSave={(v) => updateField(category === 'tyres' ? 'collection_rate' : 'effective_date', v)}
            placeholder={category === 'tyres' ? "e.g., 95%" : "e.g., 2025-01-01"}
            onEnrich={() => openEnrichment(
              category === 'tyres' ? 'collection_rate' : 'effective_date', 
              category === 'tyres' ? 'text' : 'date', 
              category === 'tyres' ? localData.collection_rate : localData.effective_date
            )}
          />
        </div>
      </div>

      {/* PROs - Editable */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-stone-800 text-sm">
            {countryCode === 'UK' ? `${categoryName} Compliance Schemes` : (category === 'tyres' ? 'Collection Schemes' : `${categoryName} PROs`)}
          </h3>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50"
            onClick={() => openEnrichment('major_pros', 'pros', localData.major_pros)}
            data-testid="enrich-pros-btn"
          >
            <Sparkles className="w-3.5 h-3.5 mr-1" />
            <span className="text-xs">AI Fill</span>
          </Button>
        </div>
        <EditablePROList 
          pros={localData.major_pros || []}
          onUpdate={(pros) => updateField('major_pros', pros)}
        />
      </div>

      {/* Scope - Editable */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-semibold text-stone-800 text-sm">
            {category === 'tyres' ? 'Covered Tyre Types' : 'Covered Products'}
          </h3>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 px-2 text-blue-500 hover:text-blue-600 hover:bg-blue-50"
            onClick={() => openEnrichment('scope', 'list', localData.scope)}
            data-testid="enrich-scope-btn"
          >
            <Sparkles className="w-3.5 h-3.5 mr-1" />
            <span className="text-xs">AI Fill</span>
          </Button>
        </div>
        <EditableScopeList 
          items={localData.scope || []}
          onUpdate={(items) => updateField('scope', items)}
          label="product"
        />
      </div>

      {/* Fee Structure - Editable */}
      <div className="card p-4">
        <h3 className="font-semibold text-stone-800 mb-2 text-sm flex items-center gap-1.5">
          <Euro className="w-4 h-4 text-green-600" />
          Fee Structure
        </h3>
        <div className="space-y-2">
          <InlineEdit 
            value={localData.fee_structure?.type}
            onSave={(v) => updateNestedField('fee_structure', 'type', v)}
            placeholder="e.g., Weight-based per material"
            onEnrich={() => openEnrichment('fee_structure.type', 'text', localData.fee_structure?.type)}
          />
          <div className="text-xs text-stone-500">
            <InlineEdit 
              value={localData.fee_structure?.base_rate}
              onSave={(v) => updateNestedField('fee_structure', 'base_rate', v)}
              placeholder="Base rate (e.g., €0.10/kg)"
              onEnrich={() => openEnrichment('fee_structure.base_rate', 'text', localData.fee_structure?.base_rate)}
            />
          </div>
        </div>
      </div>

      {/* Notes - Editable */}
      <div className="card p-4">
        <h3 className="font-semibold text-stone-800 mb-2 text-sm">Notes</h3>
        <InlineEdit 
          value={localData.notes}
          onSave={(v) => updateField('notes', v)}
          type="textarea"
          placeholder="Add notes about this waste stream..."
          onEnrich={() => openEnrichment('notes', 'text', localData.notes)}
        />
      </div>

      {/* AI Enrichment Popup */}
      <EnrichmentPopup
        isOpen={enrichmentOpen}
        onClose={() => setEnrichmentOpen(false)}
        fieldType={enrichmentField?.fieldType || 'text'}
        fieldName={enrichmentField?.fieldName || ''}
        currentValue={enrichmentField?.currentValue}
        countryCode={countryCode}
        category={category}
        onAccept={handleEnrichmentAccept}
      />
    </div>
  );
}
